import { createStore, type StoreApi } from 'zustand/vanilla';
import type { PicklistAdjustmentReason, SerialDisposition } from '../../types';
import { emptyAdjustForm, staleResetForm, type AdjustForm, type SerialRowState } from '../../utils/adjustModel';
import type { MoveSource } from '../../utils/moveModel';

/**
 * PT-6 — the Adjust dialog's editing state, ONE store per open dialog, never on `PicklistDetailPage` (UI Performance
 * gate). The dialog builds its model from it; each card re-renders only when its own figures change (its `sig`), and
 * the notes field subscribes to its own text, so a note keystroke re-renders the field alone.
 *
 * The actions follow the prototype's handlers (`adjSet` callers): typing a value back to its current figure clears
 * the edit; Reset keeps the reason and the note. PT-6b — a Move is not staged here: `openMove` names the row and the
 * Move dialog does the rest.
 */
export interface AdjustDialogState extends AdjustForm {
  /** The amber "figures changed" note: shown after a 409 until dismissed or the dialog closes. */
  stale: boolean;
  /** The footer's failure line (B4); null when none. */
  applyError: string | null;
  submitting: boolean;
  /** The row whose planned-breakdown card is open. */
  planKey: string | null;
  /** PT-6b — the row the Move dialog is open for; null when it is closed. */
  moveSource: MoveSource | null;

  setReason: (reason: PicklistAdjustmentReason) => void;
  setNotes: (notes: string) => void;
  /** A quantity box. `current` is the row's figure: typing it back clears the edit. */
  setVal: (key: string, text: string, current: number) => void;
  /** A serial's Keep / Unpick. */
  setSerMode: (key: string, mode: 'keep' | 'return') => void;
  setSerDisp: (key: string, disp: SerialDisposition) => void;
  returnAll: (keys: readonly string[]) => void;
  keepAll: () => void;
  setDisp: (displayBinId: number, disp: SerialDisposition) => void;
  /** Reset for a quantity item: every typed value and the disposition. */
  qtyReset: () => void;
  /** After a 409: drop every number, keep the reason and the note, show the amber note. */
  markStale: () => void;
  dismissStale: () => void;
  setApplyError: (text: string | null) => void;
  setSubmitting: (submitting: boolean) => void;
  setPlanKey: (key: string | null) => void;
  openMove: (source: MoveSource) => void;
  closeMove: () => void;
  /** After a Move saved: the figures are new, so every number goes; the reason and the note stay. */
  resetAfterMove: () => void;
}

const cleared = { vals: {}, disp: {}, ser: {} } as const;

export function createAdjustDialogStore(): StoreApi<AdjustDialogState> {
  return createStore<AdjustDialogState>()((set) => ({
    ...emptyAdjustForm(),
    stale: false,
    applyError: null,
    submitting: false,
    planKey: null,
    moveSource: null,

    setReason: (reason) => set({ reason }),
    setNotes: (notes) => set({ notes: notes.slice(0, 500) }),
    setVal: (key, text, current) => set((s) => {
      const vals = { ...s.vals, [key]: text };
      if (text.trim() !== '' && Number(text) === current) delete vals[key];
      // Clearing every value clears the disposition too (the prototype's onInput).
      return Object.keys(vals).length > 0 ? { vals } : { vals, disp: {} };
    }),
    setSerMode: (key, mode) => set((s) => {
      if (mode === 'keep') {
        const ser = { ...s.ser };
        delete ser[key];
        return { ser };
      }
      return { ser: { ...s.ser, [key]: { mode: 'return' } as SerialRowState } };
    }),
    setSerDisp: (key, disp) => set((s) => (s.ser[key] ? { ser: { ...s.ser, [key]: { ...s.ser[key], disp } } } : {})),
    returnAll: (keys) => set({ ser: Object.fromEntries(keys.map((k) => [k, { mode: 'return' } as SerialRowState])) }),
    keepAll: () => set({ ser: {} }),
    setDisp: (displayBinId, disp) => set((s) => ({ disp: { ...s.disp, [displayBinId]: disp } })),
    qtyReset: () => set({ ...cleared }),
    markStale: () => set((s) => ({ ...staleResetForm(s), stale: true, planKey: null, applyError: null })),
    dismissStale: () => set({ stale: false }),
    setApplyError: (applyError) => set({ applyError }),
    setSubmitting: (submitting) => set({ submitting }),
    setPlanKey: (planKey) => set({ planKey }),
    openMove: (moveSource) => set({ moveSource, planKey: null }),
    closeMove: () => set({ moveSource: null }),
    resetAfterMove: () => set({ ...cleared, moveSource: null, planKey: null, applyError: null, stale: false }),
  }));
}
